import {Link} from 'react-router-dom';
import {ReactNode} from 'react';
import {AuthLayoutFooter} from './auth-layout-footer';
import {useIsDarkMode} from '@common/ui/themes/use-is-dark-mode';
import {useTrans} from '@common/i18n/use-trans';
import {useSettings} from '@common/core/settings/use-settings';

interface AuthPageProps {
  heading?: ReactNode;
  message?: ReactNode;
  children: ReactNode;
}

const FEATURES = [
  'One link for all your social profiles',
  'Track every click with real-time analytics',
  'Custom domains & branded short links',
  'Beautiful biolink pages in minutes',
];

export function AuthLayout({heading, children, message}: AuthPageProps) {
  const {branding} = useSettings();
  const isDarkMode = useIsDarkMode();
  const {trans} = useTrans();

  return (
    <main className="flex h-screen overflow-hidden">
      {/* ── LEFT BRAND PANEL (hidden on mobile) ── */}
      <div
        className="hidden lg:flex lg:w-5/12 xl:w-2/5 flex-col justify-between relative overflow-hidden p-48 xl:p-60 h-screen"
        style={{
          backgroundColor: '#1a0800',
          backgroundImage: [
            'radial-gradient(120% 120% at 0% 0%, rgba(255,178,62,0.45), transparent 50%)',
            'radial-gradient(120% 120% at 100% 0%, rgba(255,46,99,0.28), transparent 45%)',
            'radial-gradient(140% 140% at 50% 120%, rgba(255,79,0,0.55), transparent 60%)',
          ].join(','),
        }}
      >
        {/* decorative blobs */}
        <div
          className="pointer-events-none absolute -top-80 -left-80 h-320 w-320 rounded-full opacity-10"
          style={{background: 'rgba(255,255,255,0.15)'}}
        />
        <div
          className="pointer-events-none absolute -bottom-60 -right-60 h-280 w-280 rounded-full opacity-10"
          style={{background: 'rgba(255,255,255,0.10)'}}
        />
        <div
          className="pointer-events-none absolute top-1/2 -right-40 h-200 w-200 rounded-full opacity-10"
          style={{background: 'rgba(255,255,255,0.10)'}}
        />

        {/* Logo */}
        <Link to="/" aria-label={trans({message: 'Go to homepage'})}>
          <img
            src={branding?.logo_light}
            className="h-36 w-auto"
            alt={branding?.site_name}
          />
        </Link>

        {/* Center content */}
        <div className="relative z-10">
          <h2
            className="text-3xl xl:text-4xl font-bold leading-tight mb-16"
            style={{color: 'rgb(255 255 255)'}}
          >
            One link for{' '}
            <span style={{color: '#ffb23e'}}>
              everything
            </span>{' '}
            you share.
          </h2>
          <p className="text-base mb-40" style={{color: 'rgb(255 255 255 / 0.7)'}}>
            Build your biolink page, shorten URLs, and track every click — all from one dashboard.
          </p>

          <ul className="space-y-14">
            {FEATURES.map(feature => (
              <li key={feature} className="flex items-center gap-12">
                {/* checkmark circle */}
                <span
                  className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full"
                  style={{background: 'rgba(255,79,0,0.35)'}}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M2 6l3 3 5-5"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="text-sm" style={{color: 'rgb(255 255 255 / 0.85)'}}>
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* bottom tagline */}
        <p className="relative z-10 text-xs" style={{color: 'rgb(255 255 255 / 0.45)'}}>
          Trusted by creators & businesses worldwide
        </p>
      </div>

      {/* ── RIGHT FORM PANEL ── */}
      <div className="flex flex-1 flex-col items-center h-screen overflow-y-auto px-24 py-40 md:px-48 bg">
        <div className="w-full max-w-400 my-auto">
        {/* Mobile logo */}
        <Link
          to="/"
          className="mb-32 flex lg:hidden shrink-0 justify-center"
          aria-label={trans({message: 'Go to homepage'})}
        >
          {(isDarkMode ? branding?.logo_light : (branding?.logo_dark || branding?.logo_light)) ? (
            <img
              src={isDarkMode ? branding.logo_light : (branding?.logo_dark || branding?.logo_light)}
              className="h-36 w-auto"
              alt={branding?.site_name}
            />
          ) : (
            <span className="text-xl font-bold text-main">{branding?.site_name}</span>
          )}
        </Link>
          {/* Heading */}
          {heading && (
            <div className="mb-20">
              <h1 className="text-xl font-bold text-main">{heading}</h1>
              {message && (
                <p className="mt-6 text-sm text-muted">{message}</p>
              )}
            </div>
          )}

          {/* Form card */}
          <div className="rounded-2xl border border-divider bg px-24 py-24 shadow-sm">
            {children}
          </div>

          {/* Footer */}
          <AuthLayoutFooter />
        </div>
      </div>
    </main>
  );
}
